/**
 * FinanceiroDespesasPage — visão CFO de Despesas (#22, mockup aprovado).
 *
 * REGRA-CHAVE: o CMV/CPV de energia (linha D1 "Custos de Operação", família
 * 3.1 — dominada pela categoria 3.1.1.01.003 CUSTO DAS MERCADORIAS) fica FORA
 * de TODA a composição operacional (KPIs, barras, tendência): volátil por
 * PLD, suja a análise do que a gestão controla. NOTA: a família 3.1 inclui
 * também intermediação BBCE (3.1.1.01.015) — sai junto com o D1; separá-la
 * exigiria split da linha no backend (decisão futura, reportado).
 *
 * Camadas (mesmos endpoints do mobile Fase 1, sem backend novo):
 *  1. KPIs: total operacional ex-CMV (Σ D2..D10) + Δ% vs mês anterior
 *     (comparar=mom do /api/v2/dre) + maior linha + nº lançamentos.
 *  2. COMPOSIÇÃO: barras horizontais por linha DRE (desc, valor + %), uma
 *     cor (magnitude), sentence case. Clique filtra o detalhe.
 *  3. TENDÊNCIA: Σ operacional ex-CMV dos últimos 6 meses (6 fetches leves
 *     ao /api/v2/dre em paralelo), mês atual destacado. Com linha selecionada
 *     na composição (#27), mostra a série daquela linha DRE com o total
 *     ex-CMV como barra fantasma ao fundo — MESMOS 6 fetches (os agregados
 *     já trazem todas as linhas D; só extrai a selecionada), sem request novo.
 *  4. DETALHE: lançamentos da linha selecionada (/api/v2/despesas) AGRUPADOS
 *     por categoria com subtotais + total.
 */
import { useEffect, useMemo, useState } from "react";
import { getDreV2 } from "../api/client";
import { KpiCard } from "../components/ui/KpiCard";
import { Loading } from "../components/Loading";
import { useDespesasV2 } from "../hooks/useDespesasV2";
import { useDreV2 } from "../hooks/useDreV2";
import type { DespesaDetalhadaV2, DreLinhaV2 } from "../api/types";
import { fmtBRL, fmtPct } from "../utils/formatters";
import { formatarDataCurtaBr } from "../utils/dateBr";

/** Linha D1 (CPV/CMV energia, família 3.1) — fora da visão operacional. */
const LINHA_CMV = "D1";

function currentMesRef(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Últimos n mesRefs terminando em `fim` (inclusive), ordem cronológica. */
export function _ultimosMeses(fim: string, n = 6): string[] {
  const [y, m] = fim.split("-").map(Number);
  const out: string[] = [];
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(y, m - 1 - i, 1);
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  }
  return out;
}

/** "SERVIÇOS DE TERCEIROS" -> "Serviços de terceiros" (display-only; a seed
 * segue em CAPS — sentence case é regra desta VIEW, mockup #22). */
const _ACRONIMOS = new Set(["IRPJ", "CSLL", "CMV", "CPV", "BBCE", "CCEE", "FGTS", "INSS", "MTM", "PLD"]);

export function _sentenceCase(s: string): string {
  // Siglas fiscais/mercado ficam em CAPS ("IRPJ + CSLL", nao "Irpj + csll").
  const lower = s.toLocaleLowerCase("pt-BR");
  const frase = lower.charAt(0).toLocaleUpperCase("pt-BR") + lower.slice(1);
  return frase
    .split(" ")
    .map((w) => (_ACRONIMOS.has(w.toUpperCase()) ? w.toUpperCase() : w))
    .join(" ");
}

// Cache module-level do agregado de despesas por mes (TTL 180s, padrao dos
// hooks da casa) — evita re-disparar 6 agregacoes a cada troca de aba. Guarda
// o TOTAL ex-CMV E o valor por linha DRE (#27: a serie por linha sai do MESMO
// fetch — trocar a linha selecionada nao dispara request novo).
const _SERIE_TTL_MS = 180 * 1000;

interface DreMesAgg {
  /** Σ |valor| das linhas de despesa ex-CMV (D2..D10). */
  totalExCmv: number;
  /** |valor| por código de linha DRE (inclui D1 — o chip do CPV também tem série). */
  porLinha: Record<string, number>;
}

const _serieCache = new Map<string, { expiresAt: number; promise: Promise<DreMesAgg> }>();

/** Teste-only: zera o cache da série (module-level). */
export function _invalidateSerieCache(): void {
  _serieCache.clear();
}

function _despesasMesCached(mesRef: string): Promise<DreMesAgg> {
  const now = Date.now();
  const hit = _serieCache.get(mesRef);
  if (hit && hit.expiresAt > now) return hit.promise;
  const promise = getDreV2({ regime: "competencia", periodo: "mensal", mes_ref: mesRef }).then((r) => {
    const op = r.secoes.find((s) => s.nome.toUpperCase().startsWith("OPERACIONAL"));
    const despesas = (op?.linhas ?? []).filter((l) => l.sinal === -1);
    const porLinha: Record<string, number> = {};
    for (const l of despesas) porLinha[l.codigo] = Math.abs(l.valor_atual);
    const totalExCmv = despesas
      .filter((l) => l.codigo !== LINHA_CMV)
      .reduce((s, l) => s + Math.abs(l.valor_atual), 0);
    return { totalExCmv, porLinha };
  });
  promise.catch(() => _serieCache.delete(mesRef));
  _serieCache.set(mesRef, { expiresAt: now + _SERIE_TTL_MS, promise });
  return promise;
}

function parseIso(iso: string): Date {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12, 0, 0, 0);
}

const mesCurto = (mesRef: string): string => {
  const [y, m] = mesRef.split("-").map(Number);
  return `${["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"][m - 1]}/${String(y).slice(2)}`;
};

interface Props {
  /** Linha DRE pré-selecionada vinda do drill-down do DRE Gerencial. */
  linhaInicial?: string | null;
}

export function FinanceiroDespesasPage({ linhaInicial }: Props = {}) {
  const [mesRef, setMesRef] = useState<string>(currentMesRef());
  // Linha do DETALHE: default = maior linha operacional (setada quando o DRE
  // resolve); drill do DRE Gerencial (linhaInicial) ganha — inclusive D1
  // (detalhe do CPV segue acessível; só a COMPOSIÇÃO exclui).
  const [linhaSel, setLinhaSel] = useState<string | null>(linhaInicial ?? null);

  const dre = useDreV2({ regime: "competencia", periodo: "mensal", mesRef, comparar: "mom" });

  // ---- Linhas de despesa (inclui D1 p/ lookup de nome) e operacionais ex-CMV ----
  const linhasDesp: DreLinhaV2[] = useMemo(() => {
    const op = dre.data?.secoes.find((s) => s.nome.toUpperCase().startsWith("OPERACIONAL"));
    return (op?.linhas ?? []).filter((l) => l.sinal === -1);
  }, [dre.data]);
  const linhasOp: DreLinhaV2[] = useMemo(
    () => linhasDesp.filter((l) => l.codigo !== LINHA_CMV),
    [linhasDesp],
  );
  const totalOp = linhasOp.reduce((s, l) => s + Math.abs(l.valor_atual), 0);
  const totalOpAnterior = linhasOp.reduce(
    (s, l) => s + Math.abs(l.valor_comparacao ?? 0),
    0,
  );
  const temComparacao = linhasOp.some((l) => l.valor_comparacao != null);
  const deltaPct = temComparacao && totalOpAnterior > 0
    ? ((totalOp - totalOpAnterior) / totalOpAnterior) * 100
    : null;
  const nLancamentos = linhasOp.reduce((s, l) => s + l.qtd_lancamentos, 0);
  const ordenadasDesc = useMemo(
    () => [...linhasOp].sort((a, b) => Math.abs(b.valor_atual) - Math.abs(a.valor_atual)),
    [linhasOp],
  );
  const maiorLinha = ordenadasDesc[0] ?? null;

  // Detalhe: seleção manual ganha; default = maior linha (derivado, sem
  // efeito — troca de mês não rouba a seleção do usuário).
  const linhaEfetiva = linhaSel ?? maiorLinha?.codigo ?? "D2";

  // ---- Tendencia 6m: cache module-level + allSettled (1 mes falho vira
  // "—", nao mata o painel — review 03/07) ----
  const [serie6m, setSerie6m] = useState<{ mes: string; agg: DreMesAgg | null }[] | null>(null);
  useEffect(() => {
    let cancel = false;
    setSerie6m(null);
    const meses = _ultimosMeses(mesRef, 6);
    Promise.allSettled(meses.map((m) => _despesasMesCached(m))).then((rs) => {
      if (cancel) return;
      setSerie6m(
        rs.map((r, i) => ({
          mes: meses[i],
          agg: r.status === "fulfilled" ? r.value : null,
        })),
      );
    });
    return () => { cancel = true; };
  }, [mesRef]);
  // #27: SÓ clique explícito (ou drill do DRE) dirige a tendência — o default
  // do detalhe (maior linha) NÃO troca a série; sem seleção = global de hoje.
  const linhaTend = linhaSel;
  const nomeLinhaTend = linhaTend
    ? _sentenceCase(linhasDesp.find((l) => l.codigo === linhaTend)?.nome ?? linhaTend)
    : null;
  const valorLinhaMes = (agg: DreMesAgg): number => agg.porLinha[linhaTend ?? ""] ?? 0;
  // Escala pelo maior entre global e linha (D1 via chip pode PASSAR o ex-CMV).
  const maxSerie = Math.max(
    1,
    ...(serie6m ?? []).flatMap((s) =>
      s.agg ? [s.agg.totalExCmv, linhaTend ? valorLinhaMes(s.agg) : 0] : [],
    ),
  );

  // ---- Detalhe (linha selecionada), agrupado por categoria ----
  const detalhe = useDespesasV2({
    linhaDre: linhaEfetiva,
    regime: "competencia",
    periodo: "mensal",
    mesRef,
  });
  const grupos = useMemo(() => {
    const rows: DespesaDetalhadaV2[] = detalhe.data?.despesas ?? [];
    const by = new Map<string, { nome: string; total: number; rows: DespesaDetalhadaV2[] }>();
    for (const r of rows) {
      const nome = r.categoria_nome ?? r.categoria_codigo ?? "Sem categoria";
      const g = by.get(nome) ?? { nome, total: 0, rows: [] };
      g.total += r.valor;
      g.rows.push(r);
      by.set(nome, g);
    }
    const out = [...by.values()].sort((a, b) => b.total - a.total);
    for (const g of out) {
      g.rows.sort((a, b) => (b.data_vencimento ?? "").localeCompare(a.data_vencimento ?? ""));
    }
    return out;
  }, [detalhe.data]);
  const totalDetalhe = detalhe.data?.total_valor ?? 0;
  const linhaSelNome = _sentenceCase(
    linhasOp.find((l) => l.codigo === linhaEfetiva)?.nome
      ?? detalhe.data?.linha_dre_nome
      ?? linhaEfetiva,
  );
  // #93/teto: o backend agora DECLARA o corte (e os totais são pré-corte);
  // fallback pro heurístico >=500 só pra resposta velha em cache sem `teto`.
  const teto = detalhe.data?.teto ?? null;
  const capped = teto
    ? teto.truncado
    : (detalhe.data?.total_lancamentos ?? 0) >= 500;

  return (
    <div data-testid="financeiro-despesas-page">
      <div className="mb-6 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-black text-white font-sans">Despesas operacionais</h1>
          <p className="text-white/50 text-sm mt-1">
            Visão do que a gestão controla — CMV/CPV de energia (D1) fica fora.
            Origem: Omie (real).
          </p>
        </div>
        <input
          type="month"
          value={mesRef}
          onChange={(e) => { if (e.target.value) setMesRef(e.target.value); }}
          data-testid="despesas-mes-picker"
          aria-label="Mês de referência"
          className="bg-indra-azul-deep border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white/80 [color-scheme:dark]"
        />
      </div>

      {dre.error ? (
        <div
          className="rounded-xl border border-indra-negativo/30 bg-indra-negativo/10 p-6 text-sm text-white/80"
          data-testid="despesas-error"
        >
          Não foi possível carregar as despesas. Verifique a conexão e tente novamente.
        </div>
      ) : !dre.data || dre.isLoading ? (
        <div className="flex items-center justify-center py-20" data-testid="despesas-loading">
          <Loading label="Carregando despesas…" />
        </div>
      ) : (
        <>
          {/* 1 — KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 items-stretch" data-testid="despesas-kpis">
            <KpiCard
              label="Total operacional no mês (ex-CMV)"
              value={fmtBRL(totalOp, { abbreviate: true })}
              sub={
                deltaPct !== null
                  ? `${deltaPct >= 0 ? "+" : "−"}${fmtPct(Math.abs(deltaPct), 1)} vs mês anterior`
                  : "sem base de comparação"
              }
              subTone={deltaPct !== null && deltaPct > 0 ? "negative" : "muted"}
              highlight
              testId="despesas-kpi-total"
            />
            <KpiCard
              label="Maior linha"
              value={maiorLinha ? fmtBRL(Math.abs(maiorLinha.valor_atual), { abbreviate: true }) : "—"}
              sub={maiorLinha ? _sentenceCase(maiorLinha.nome) : undefined}
              subTone="muted"
              testId="despesas-kpi-maior"
            />
            <KpiCard
              label="Lançamentos no mês"
              value={String(nLancamentos)}
              sub="linhas operacionais (sem CPV de energia)"
              subTone="muted"
              testId="despesas-kpi-lancamentos"
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {/* 2 — COMPOSIÇÃO por linha DRE */}
            <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5" data-testid="despesas-composicao">
              <h3 className="text-white font-semibold text-sm mb-1">Composição por linha DRE</h3>
              <p className="text-xs text-white/60 mb-3">
                Toque numa linha para detalhar. CMV de energia (D1) excluído.
              </p>
              {ordenadasDesc.length === 0 ? (
                <div className="text-white/60 text-sm" data-testid="despesas-composicao-vazia">
                  Sem despesas operacionais no mês.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {ordenadasDesc.map((l) => {
                    const v = Math.abs(l.valor_atual);
                    const pct = totalOp > 0 ? (v / totalOp) * 100 : 0;
                    const ativa = linhaEfetiva === l.codigo;
                    return (
                      <button
                        key={l.codigo}
                        type="button"
                        onClick={() => setLinhaSel(l.codigo)}
                        aria-pressed={ativa}
                        data-testid={`despesas-barra-${l.codigo}`}
                        className={
                          "w-full text-left rounded-lg px-3 py-2 border transition-colors " +
                          (ativa
                            ? "border-indra-turquesa/50 bg-indra-turquesa/10"
                            : "border-transparent hover:bg-white/5 active:bg-white/10")
                        }
                      >
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <span className="text-sm text-slate-200 truncate">{_sentenceCase(l.nome)}</span>
                          <span className="text-sm font-mono tabular-nums text-white whitespace-nowrap">
                            {fmtBRL(v, { abbreviate: true })}
                            <span className="text-white/50 ml-1.5">{fmtPct(pct, 1)}</span>
                          </span>
                        </div>
                        {/* uma cor só — magnitude e' a largura */}
                        <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indra-turquesa/70 rounded-full"
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              <button
                type="button"
                onClick={() => setLinhaSel(LINHA_CMV)}
                aria-pressed={linhaEfetiva === LINHA_CMV}
                data-testid="despesas-ver-cpv"
                className={
                  "mt-3 text-xs rounded-md px-2.5 py-1.5 border " +
                  (linhaEfetiva === LINHA_CMV
                    ? "border-indra-turquesa/50 text-indra-turquesa bg-indra-turquesa/10"
                    : "border-white/10 text-white/60 hover:text-white/80 active:bg-white/10")
                }
              >
                Ver detalhe do CPV de energia (D1) — fora da composição
              </button>
            </div>

            {/* 3 — TENDÊNCIA 6 meses (#27: linha selecionada dirige; global ao fundo) */}
            <div className="bg-indra-azul-surface rounded-xl border border-white/10 p-5" data-testid="despesas-tendencia">
              <h3 className="text-white font-semibold text-sm mb-1" data-testid="despesas-tendencia-titulo">
                {nomeLinhaTend ? `Tendência — ${nomeLinhaTend}` : "Tendência — últimos 6 meses"}
              </h3>
              <p className="text-xs text-white/60 mb-3">
                {nomeLinhaTend
                  ? "Linha selecionada por mês; total ex-CMV ao fundo (cinza)."
                  : "Total operacional ex-CMV por mês."}
              </p>
              {!serie6m ? (
                <div className="text-white/60 text-sm">…</div>
              ) : (
                <div className="flex items-end gap-2 h-40" data-testid="despesas-tendencia-barras">
                  {serie6m.map((s) => {
                    const atual = s.mes === mesRef;
                    const total = s.agg?.totalExCmv ?? null;
                    const valor = s.agg === null ? null : linhaTend ? valorLinhaMes(s.agg) : total;
                    const corBarra = atual ? "bg-indra-turquesa" : "bg-indra-turquesa/35";
                    return (
                      <div key={s.mes} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                        <span className="text-[11px] font-mono text-white/70 whitespace-nowrap">
                          {valor !== null ? fmtBRL(valor, { abbreviate: true }) : "—"}
                        </span>
                        <div className="w-full relative" style={{ height: "6.5rem" }}>
                          {/* fantasma: total ex-CMV do mês, só no modo por-linha */}
                          {linhaTend && total !== null && (
                            <div
                              className="absolute bottom-0 w-full rounded-t bg-white/10"
                              data-testid={`despesas-tend-fantasma-${s.mes}`}
                              style={{ height: `${Math.max(3, (total / maxSerie) * 100)}%` }}
                              title={`${mesCurto(s.mes)}: total ex-CMV ${fmtBRL(total, { decimals: 0 })}`}
                            />
                          )}
                          <div
                            className={
                              "absolute bottom-0 w-full rounded-t " +
                              (valor === null ? "bg-white/10" : corBarra)
                            }
                            style={{ height: `${Math.max(3, ((valor ?? 0) / maxSerie) * 100)}%` }}
                            title={
                              valor !== null
                                ? `${mesCurto(s.mes)}: ${nomeLinhaTend ? `${nomeLinhaTend} ` : ""}${fmtBRL(valor, { decimals: 0 })}`
                                : `${mesCurto(s.mes)}: falha ao carregar`
                            }
                          />
                        </div>
                        <span className={"text-xs " + (atual ? "text-indra-turquesa font-semibold" : "text-white/60")}>
                          {mesCurto(s.mes)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* 4 — DETALHE agrupado por categoria */}
          <div
            className="bg-indra-azul-surface rounded-xl border border-white/10 overflow-hidden"
            data-testid="despesas-table"
          >
            <div className="px-5 py-3 border-b border-white/10">
              <h3 className="text-white font-semibold text-sm">
                Detalhe — {linhaSelNome}
              </h3>
              <p className="text-xs text-white/60 mt-0.5">
                {detalhe.data?.total_lancamentos ?? 0} lançamento
                {(detalhe.data?.total_lancamentos ?? 0) === 1 ? "" : "s"}, agrupados por categoria
                {capped
                  ? teto
                    ? ` (listando os ${teto.devolvidas} mais recentes de ${teto.total}; ` +
                      "grupos somam só os listados — o total acima é o completo)"
                    : " (amostra de 500 — refine o período)"
                  : ""}
              </p>
            </div>
            {detalhe.isLoading ? (
              <div className="text-center text-white/60 py-10">…</div>
            ) : grupos.length === 0 ? (
              <div className="text-center text-white/60 py-10" data-testid="despesas-table-empty">
                Nenhum lançamento em {linhaSelNome} no período selecionado.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px]">
                  <thead className="bg-indra-azul-deep/40">
                    <tr className="border-b border-white/10">
                      <th className="text-left px-4 py-3 text-[11px] tracking-wider text-white/60 font-semibold">Data venc.</th>
                      <th className="text-left px-4 py-3 text-[11px] tracking-wider text-white/60 font-semibold">Fornecedor</th>
                      <th className="text-right px-4 py-3 text-[11px] tracking-wider text-white/60 font-semibold">Valor (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grupos.map((g) => (
                      <FragmentoGrupo key={g.nome} grupo={g} />
                    ))}
                    <tr className="bg-indra-turquesa/10 border-t border-indra-turquesa/30">
                      <td className="px-4 py-2.5 text-sm font-bold text-indra-turquesa" colSpan={2} data-testid="despesas-total-label">
                        Total — {linhaSelNome}
                        {capped && (
                          <span className="text-[11px] font-normal text-amber-300/90 ml-2">
                            (total da linha inteira; tabela mostra amostra de 500)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-right font-mono tabular-nums font-bold text-indra-turquesa" data-testid="despesas-total-valor">
                        {fmtBRL(totalDetalhe, { decimals: 0 })}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function FragmentoGrupo({ grupo }: {
  grupo: { nome: string; total: number; rows: DespesaDetalhadaV2[] };
}) {
  return (
    <>
      <tr className="bg-white/5 border-b border-white/10" data-testid={`despesas-grupo-${grupo.nome}`}>
        <td className="px-4 py-2 text-xs font-semibold text-white/80" colSpan={2}>
          {grupo.nome}
        </td>
        <td className="px-4 py-2 text-xs text-right font-mono tabular-nums font-semibold text-white/80">
          {fmtBRL(grupo.total, { decimals: 0 })}
        </td>
      </tr>
      {grupo.rows.map((d) => (
        <tr key={d.id} className="border-b border-white/5" data-testid={`despesas-row-${d.id}`}>
          <td className="px-4 py-2 text-sm text-white/80 whitespace-nowrap">
            {d.data_vencimento ? formatarDataCurtaBr(parseIso(d.data_vencimento)) : "—"}
          </td>
          <td
            className="px-4 py-2 text-sm text-white font-medium max-w-[320px] truncate"
            title={d.fornecedor_nome ?? undefined}
          >
            {d.fornecedor_nome ?? "—"}
          </td>
          <td className="px-4 py-2 text-sm text-right font-mono tabular-nums text-white">
            {fmtBRL(d.valor, { decimals: 0 })}
          </td>
        </tr>
      ))}
    </>
  );
}
